import { ExtensionProperty, IProperty, Nullable, PropertyType } from "@gltf-transform/core3";
import { EXT_LIGHTS_IES, IES_PROFILE_MIME_TYPE } from "../constants";

interface ILightProfile extends IProperty {
	/** The URI (or IRI) of the light profile. */
	uri: string;
	/** The IES light profile string representation. Persisted as buffer. */
	iesProfile: string | null;
	/** The index of the bufferView object that contains the IES light profile file. */
	bufferView: number | null;
	/** The light profile's media type. Must be "application/x-ies-lm-63". */
	mimeType: typeof IES_PROFILE_MIME_TYPE;
	/** glTF name of the light profile. */
	name: string;
}

/**
 * Defines a light profile attached to the root extension.
 * @see https://github.com/KhronosGroup/glTF/tree/main/extensions/2.0/Vendor/EXT_lights_ies#defining-light-profiles
 */
export class LightProfile extends ExtensionProperty<ILightProfile> {
	public static EXTENSION_NAME = EXT_LIGHTS_IES;
	public extensionName = EXT_LIGHTS_IES;
	public propertyType = "LightProfile";
	public parentTypes = [PropertyType.NODE];

	public static MIMEType: typeof IES_PROFILE_MIME_TYPE = IES_PROFILE_MIME_TYPE;

	protected init(): void {
		this.extensionName = EXT_LIGHTS_IES;
		this.propertyType = "LightProfile";
		this.parentTypes = [PropertyType.NODE];
	}

	protected getDefaults(): Nullable<ILightProfile> {
		return Object.assign(super.getDefaults() as IProperty, {
			uri: null,
			iesProfile: null,
			bufferView: null,
			mimeType: null,
			name: "",
		});
	}

	/** Returns the URI (e.g. 'path/to/file.ies') for this light profile. */
	public getUri(): string {
		return (this as any).get("uri");
	}

	/** Sets the URI (e.g. 'path/to/file.ies') for this light profile. */
	public setUri(uri: string): this {
		return (this as any).set("uri", uri);
	}

	/** Returns the light profile string representation. */
	public getIesProfile(): string | null {
		return (this as any).get("iesProfile");
	}

	/** Sets the light profile string representation */
	public setIesProfile(iesProfile: string): this {
		return (this as any).set('iesProfile', iesProfile);
	}

	public getMimeType(): typeof IES_PROFILE_MIME_TYPE {
		return (this as any).get("mimeType");
	}

	public setMimeType(mimeType: typeof IES_PROFILE_MIME_TYPE): this {
		return (this as any).set("mimeType", mimeType);
	}

	public getBufferView(): number | null {
		return (this as any).get("bufferView");
	}

	public setBufferView(index: number): this {
		return (this as any).set("bufferView", index);
	}
}